import { DateTime } from 'luxon'
import CameraDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/influx/CameraDimensionResolver.js'
import LabelDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/influx/LabelDimensionResolver.js'
import RegionDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/influx/RegionDimensionResolver.js'
import FallbackDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/FallbackDimensionResolver.js'
import i18n from '@/plugins/i18n.js'
import VMSInstanceGUIDResolver from '@/composables/charts/influx-charts/dimension-resolvers/influx/VMSInstanceGUIDResolver.js'

const { t } = i18n.global
const supportedResolvers = [
  CameraDimensionResolver,
  LabelDimensionResolver,
  RegionDimensionResolver,
  VMSInstanceGUIDResolver,
  FallbackDimensionResolver,
]

/**
 * Used to transform a raw data result from the InfluxAPI into data highcharts can understand.
 *
 */
class InfluxQueryResultPipeline {
  constructor(dataTransformPlugins = [], query) {
    this.dataTransformPlugins = dataTransformPlugins
    this.query = query
  }

  /**
   * Find out all the resolvers that are necessary for the given data point.
   * Should be called only once, and only with the first datapoint.
   * After finding all the resolvers necessary, it prepares them so that they can actually resolve tag values
   * @param dataPoint
   * @returns {Promise<void>}
   */
  async prepareResolvers(dataPoint) {
    const relevantResolvers = supportedResolvers.filter(resolver => resolver.isRelevantForCurrentInstance())
    const keys = Object.keys(dataPoint).filter(key => key !== 'time' && key !== 'y')
    this.resolverLookup = {}
    for (const key of keys) {
      for (const resolverClass of relevantResolvers) {
        const resolver = new resolverClass()
        if (await resolver.doesTagKeyMatch(key, dataPoint, this.query)) {
          this.resolverLookup[key] = resolver
          break
        }
      }
    }
    await Promise.all(Object.values(this.resolverLookup).map(resolver => resolver.prepareResolver()))
  }

  /**
   * Takes Raw Data and returns data that highcharts can understand.
   * @param rawData
   * @returns {Promise<unknown[]>}
   */
  async prepareData(rawData) {
    if (rawData.length === 0) return []
    //const filteredRawData = rawData.filter(({y}) => y!== null)
    const firstDataPoint = rawData[0]
    await this.prepareResolvers(firstDataPoint)

    const seriesDictionary = {}

    const tagKeys = Object.keys(firstDataPoint).filter(key => key !== 'time' && key !== 'y')

    const useTotalKey = tagKeys.length === 0
    const totalTagValue = t('charts.total')

    const humanizedTagValues = {}
    for (const key of tagKeys) {
      humanizedTagValues[key] = {}
    }

    for (const rawDataPoint of rawData) {
      const y = rawDataPoint.y
      if (y === null) continue
      const x = Number.isInteger(rawDataPoint.time) ? rawDataPoint.time : DateTime.fromISO(rawDataPoint.time).toMillis()
      const humanizedTagValueForDataPoint = []

      for (const tagKey of tagKeys) {
        const tagValue = rawDataPoint[tagKey]
        if (!Object.keys(humanizedTagValues[tagKey]).includes(tagValue))
          humanizedTagValues[tagKey][tagValue] = await this.resolverLookup[tagKey].humanizeTagValue(tagValue)
        const humanizedTagValue = humanizedTagValues[tagKey][tagValue]
        humanizedTagValueForDataPoint.push(humanizedTagValue)
      }

      const seriesKey = useTotalKey ? totalTagValue : humanizedTagValueForDataPoint.join(', ')
      if (!seriesDictionary[seriesKey])
        seriesDictionary[seriesKey] = {
          data: [],
          name: seriesKey,
        }
      seriesDictionary[seriesKey].data.push({ x, y })
    }
    const series = Object.values(seriesDictionary)
    series.sort((a, b) => a.name.localeCompare(b.name))
    if (this.dataTransformPlugins?.length) {
      let transformedSeries = series
      for (const dataTransformPlugin of this.dataTransformPlugins) {
        transformedSeries = await dataTransformPlugin.transformData(transformedSeries)
      }
      return transformedSeries
    }
    return series
  }
}

export default InfluxQueryResultPipeline
